import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { useField } from 'formik';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import ScopeContext from '@sophocles/app/ScopeContext';
import {
  loadReference,
  selectAvailableLoadTypeIdsForCustomerType,
  selectRelayLoadTypes,
} from '@sophocles/features/reference';
import { resolveScopeCustomerTypeId } from '@sophocles/hooks/relayLoadTerm';
import { filterRelayLoadTypeOptions } from '@sophocles/views/sites/components/filterRelayLoadTypeOptions';

function PanelTypeField({
  required = false,
  size = 'normal',
  fullWidth = false,
  hiddenLabel = false,
  helperText = null,
  defaultValue = '',
  label,
  variant,
  ...props
}) {
  const [field, meta] = useField(props);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Relay load types come from the shared odl-app reference catalog. Ensure
  // it's loaded (idempotent — the thunk no-ops once the catalog is cached) so
  // the dropdown is populated wherever this field is hosted, including the
  // dashboard route where the legacy entity stores are never fetched.
  useEffect(() => {
    dispatch(loadReference());
  }, [dispatch]);

  const loadTypeOptions = useSelector(selectRelayLoadTypes);

  // Availability is scoped to the in-scope customer's CustomerType, read via
  // ScopeContext — the same channel useRelayLoadTerm uses for per-customer
  // term resolution.
  const scope = useContext(ScopeContext);
  const customerTypeId = resolveScopeCustomerTypeId(scope);
  const availableLoadTypeIds = useSelector(selectAvailableLoadTypeIdsForCustomerType(customerTypeId));

  const filteredLoadTypeOptions = () => {
    const allOptions = loadTypeOptions;
    const filtered = allOptions.filter(opt =>
      filterRelayLoadTypeOptions(opt, {
        availableLoadTypeIds,
        relayNumber: props.relayNumber,
        isBillboard: props.isBillboard,
      }),
    );

    // When the saved value is not in the filtered list, render it as a
    // disabled option so the select is never blank for existing data.
    const savedValue = field.value;
    const savedIsVisible = savedValue == null || filtered.some(opt => opt.id === savedValue);
    const savedOpt = !savedIsVisible && allOptions.find(opt => opt.id === savedValue);

    const toMenuItem = (opt, disabled = false) => {
      const loadTypeName = _.get(opt, 'name', 'Unknown');
      const loadTypeKey = loadTypeName.toLowerCase().replace(' ', '_');
      const loadTypeNameFinal = t(`loadTypes.${loadTypeKey}`, { defaultValue: loadTypeName });
      return (
        <MenuItem key={`load-type-option-${_.get(opt, 'id')}`} value={_.get(opt, 'id')} disabled={disabled}>
          {loadTypeNameFinal}
        </MenuItem>
      );
    };

    const items = filtered.map(opt => toMenuItem(opt));
    if (savedOpt) {
      items.unshift(toMenuItem(savedOpt, true));
    }
    return items;
  };

  const fieldLabel = label ?? t('loadTypes.panelType');

  return (
    <FormControl
      required={required}
      fullWidth={fullWidth}
      hiddenLabel={hiddenLabel}
      size={size}
      variant={variant}
      error={meta.error != null}
    >
      {!hiddenLabel && (
        <InputLabel id="panel-type-select-label" htmlFor="panel-type-select">
          {fieldLabel}
        </InputLabel>
      )}
      <Select
        labelId={hiddenLabel ? undefined : 'panel-type-select-label'}
        id="panel-type-select"
        value={field.value}
        defaultValue={defaultValue}
        label={hiddenLabel ? undefined : fieldLabel}
        variant={variant}
        onChange={event => {
          props.onChange(event);
          field.onChange(event);
        }}
        onBlur={event => {
          event.persist();
          event.target = { value: field.value, name: field.name };
          field.onBlur(event);
        }}
        inputProps={{
          name: props.name,
        }}
      >
        {filteredLoadTypeOptions()}
      </Select>
      {(helperText || (meta.touched && meta.error)) && (
        <FormHelperText>{meta.touched && meta.error ? meta.error : helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

PanelTypeField.propTypes = {
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  fullWidth: PropTypes.bool,
  hiddenLabel: PropTypes.bool,
  helperText: PropTypes.string,
  defaultValue: PropTypes.string,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  isBillboard: PropTypes.bool,
  relayNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default PanelTypeField;
